import React, { useState, useEffect } from 'react'
import './Homepage.css'

function Homepage() {
    const [mriData, setMriData] = useState([]);
    const [ctData, setCtData] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [type, setType] = useState('MRI');
    const [loading, setLoading] = useState(true);
    const [sortType, setSortType] = useState('mri-wait-time');
    const [userAddress, setUserAddress] = useState('');
    const [calculatingDistances, setCalculatingDistances] = useState(false);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Check if Google Maps is loaded
    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max
        
        const checkGoogleMaps = () => {
            if (window.google && window.google.maps) {
                console.log('Google Maps API loaded successfully');
                setGoogleMapsLoaded(true);
            } else {
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(checkGoogleMaps, 100);
                } else {
                    console.error('Google Maps API failed to load after 10 seconds');
                    // Check if API key is still placeholder
                    const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
                    if (scripts.length > 0) {
                        const src = scripts[0].src;
                        if (src.includes('YOUR_API_KEY')) {
                            console.error('API key is still set to YOUR_API_KEY placeholder');
                            alert('Please replace YOUR_API_KEY in wto/public/index.html with your actual Google Maps API key');
                        }
                    }
                    setGoogleMapsLoaded(false);
                }
            }
        };
        checkGoogleMaps();
    }, []);

    // Load data - each user gets their own independent state
    useEffect(() => {
        Promise.all([
            fetch('/api/wait_times.php?type=MRI').then(res => res.json()).catch(err => { console.error('MRI fetch error:', err); return []; }),
            fetch('/api/wait_times.php?type=CT').then(res => res.json()).catch(err => { console.error('CT fetch error:', err); return []; })
        ]).then(([mri, ct]) => {
            // Handle empty or error responses
            if (!mri || !Array.isArray(mri)) mri = [];
            if (!ct || !Array.isArray(ct)) ct = [];
            
            // Fix misaligned addresses where distance ended up in address field
            const fixAddresses = (hospitals) => {
                return hospitals.map(h => {
                    // Check if address field contains "km" (indicating it's a distance, not an address)
                    if (h.address && h.address.toLowerCase().includes('km')) {
                        // Check if overall_wait_time_percent looks like an address (contains street number, city, postal code)
                        const overallWaitTime = h.overall_wait_time_percent || '';
                        if (overallWaitTime && 
                            /^\d+/.test(overallWaitTime) && // Starts with a number (street number)
                            /[A-Z]{2}\s+[A-Z0-9]{3}/i.test(overallWaitTime)) { // Contains province and postal code pattern (case-insensitive)
                            // Swap: use overall_wait_time_percent as address
                            return {
                                ...h,
                                address: overallWaitTime
                            };
                        }
                    }
                    return h;
                });
            };
            
            // Filter out unwanted entries
            const filterHospitals = (hospitals) => {
                return hospitals.filter(h => {
                    if (!h.hospital) return true; // Keep entries without hospital name for now
                    
                    const hospitalLower = h.hospital.toLowerCase();
                    
                    // Remove entries with "Learn why some data is not available" or hqontario.ca link
                    if (hospitalLower.includes('learn why some data is not available') ||
                        hospitalLower.includes('hqontario.ca')) {
                        return false;
                    }
                    
                    // Remove hospitals containing "(the)" (case-insensitive)
                    if (hospitalLower.includes('(the)')) {
                        return false;
                    }
                    
                    return true;
                });
            };
            
            // Fix addresses before filtering
            const fixedMri = fixAddresses(mri);
            const fixedCt = fixAddresses(ct);
            
            const filteredMri = filterHospitals(fixedMri);
            const filteredCt = filterHospitals(fixedCt);
            
            // Separate hospitals with valid data from those with insufficient data
            const sortWithInsufficientData = (hospitals) => {
                const valid = hospitals.filter(h => h.calculated_wait_time_days !== null);
                const insufficient = hospitals.filter(h => h.calculated_wait_time_days === null);
                
                // Sort valid hospitals by wait time (shortest to longest)
                valid.sort((a, b) => a.calculated_wait_time_days - b.calculated_wait_time_days);
                
                // Return valid hospitals first, then insufficient data hospitals at the bottom
                return [...valid, ...insufficient];
            };
            
            const sortedMri = sortWithInsufficientData(filteredMri);
            const sortedCt = sortWithInsufficientData(filteredCt);
            
            setMriData(sortedMri);
            setCtData(sortedCt);
            // Set initial display to MRI data sorted by wait time
            setDisplayData(sortedMri);
            setLoading(false);
        }).catch(err => {
            console.error('Error:', err);
            setLoading(false);
        });
    }, []);

    // Geocode address to get coordinates
    const geocodeAddress = (address) => {
        return new Promise((resolve, reject) => {
            if (!window.google || !window.google.maps) {
                reject(new Error('Google Maps API not loaded'));
                return;
            }
            
            console.log('Attempting to geocode:', address);
            const geocoder = new window.google.maps.Geocoder();
            const timeout = setTimeout(() => {
                console.error('Geocoding timeout after 15 seconds');
                reject(new Error('Geocoding timeout. This usually means:\n1. Your API key is invalid or missing\n2. Geocoding API is not enabled in Google Cloud Console\n3. API key restrictions are blocking the request\n\nPlease check wto/public/index.html and your Google Cloud Console settings.'));
            }, 15000);
            
            geocoder.geocode({ address: address }, (results, status) => {
                clearTimeout(timeout);
                console.log('Geocoding response status:', status);
                
                if (status === 'OK' && results[0]) {
                    console.log('Geocoding successful:', results[0].formatted_address);
                    resolve(results[0].geometry.location);
                } else {
                    let errorMsg = 'Geocoding failed: ' + status;
                    if (status === 'REQUEST_DENIED') {
                        errorMsg = 'API key invalid or Geocoding API not enabled.\n\nTo fix:\n1. Go to Google Cloud Console\n2. Enable "Geocoding API"\n3. Check your API key restrictions\n4. Verify the key in wto/public/index.html';
                    } else if (status === 'ZERO_RESULTS') {
                        errorMsg = 'Address not found. Please try a more specific address (e.g., "Toronto, ON" or "123 Main St, Toronto, ON").';
                    } else if (status === 'OVER_QUERY_LIMIT') {
                        errorMsg = 'API quota exceeded. Check your Google Cloud Console billing and quotas.';
                    } else if (status === 'INVALID_REQUEST') {
                        errorMsg = 'Invalid address format. Please try a different address.';
                    }
                    console.error('Geocoding error:', errorMsg);
                    reject(new Error(errorMsg));
                }
            });
        });
    };

    // Calculate distance using Google Maps Distance Matrix API
    // Note: API limit is 25 destinations per request, so we batch them
    const calculateDistances = async (userAddr, hospitals) => {
        if (!googleMapsLoaded || !window.google || !window.google.maps) {
            alert('Google Maps API is not loaded. Please check your API key in index.html');
            setCalculatingDistances(false);
            return hospitals;
        }

        // Check if API key is valid
        const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
        if (scripts.length > 0) {
            const src = scripts[0].src;
            if (src.includes('YOUR_API_KEY') || src.includes('key=') && src.split('key=')[1].split('&')[0] === 'YOUR_API_KEY') {
                alert('Please replace YOUR_API_KEY in wto/public/index.html with your actual Google Maps API key.\n\nAfter updating, refresh the page.');
                setCalculatingDistances(false);
                return hospitals;
            }
        }

        setCalculatingDistances(true);
        console.log('Starting distance calculation for:', userAddr);
        
        try {
            // Geocode user address with shorter timeout for faster feedback
            console.log('Geocoding address...');
            const userLocation = await Promise.race([
                geocodeAddress(userAddr),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Geocoding is taking too long. Your API key may be invalid or Geocoding API may not be enabled.')), 10000)
                )
            ]);
            console.log('Geocoding successful, location:', userLocation);
            
            // Distance Matrix API allows max 25 destinations per request - batch them
            const batchSize = 25;
            const batches = [];
            for (let i = 0; i < hospitals.length; i += batchSize) {
                batches.push(hospitals.slice(i, i + batchSize));
            }
            
            console.log(`Processing ${batches.length} batches of hospitals`);
            const service = new window.google.maps.DistanceMatrixService();
            const allResults = [];
            
            // Process each batch
            for (let batchNum = 0; batchNum < batches.length; batchNum++) {
                const batch = batches[batchNum];
                const destinations = batch.map(h => h.address);
                
                console.log(`Processing batch ${batchNum + 1}/${batches.length}`);
                
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        console.error('Batch timeout');
                        reject(new Error('Request timeout for batch ' + (batchNum + 1)));
                    }, 20000); // 20 second timeout per batch
                    
                    service.getDistanceMatrix(
                        {
                            origins: [userLocation],
                            destinations: destinations,
                            travelMode: window.google.maps.TravelMode.DRIVING,
                            unitSystem: window.google.maps.UnitSystem.METRIC,
                        },
                        (response, status) => {
                            clearTimeout(timeout);
                            console.log(`Batch ${batchNum + 1} response status:`, status);
                            
                            if (status === 'OK') {
                                const batchResults = batch.map((hospital, index) => {
                                    const element = response.rows[0].elements[index];
                                    if (element.status === 'OK') {
                                        const distanceKm = element.distance.value / 1000;
                                        return {
                                            ...hospital,
                                            calculatedDistance: distanceKm,
                                            distance: `${distanceKm.toFixed(1)} km`
                                        };
                                    } else {
                                        console.warn(`Failed for ${hospital.hospital}:`, element.status);
                                        return {
                                            ...hospital,
                                            calculatedDistance: 9999,
                                            distance: 'Unable to calculate'
                                        };
                                    }
                                });
                                allResults.push(...batchResults);
                                console.log(`Batch ${batchNum + 1} completed`);
                                resolve();
                            } else {
                                console.error('Distance Matrix error:', status);
                                if (status === 'REQUEST_DENIED') {
                                    alert('Distance Matrix API request denied. Check your API key and ensure Distance Matrix API is enabled.');
                                }
                                // Continue with other batches even if one fails
                                const batchResults = batch.map(hospital => ({
                                    ...hospital,
                                    calculatedDistance: 9999,
                                    distance: 'Error'
                                }));
                                allResults.push(...batchResults);
                                resolve(); // Don't reject, continue with other batches
                            }
                        }
                    );
                });
            }
            
            // Sort by calculated distance
            allResults.sort((a, b) => a.calculatedDistance - b.calculatedDistance);
            console.log('Distance calculation complete');
            setCalculatingDistances(false);
            return allResults;
            
        } catch (error) {
            console.error('Error calculating distances:', error);
            setCalculatingDistances(false);
            alert('Error calculating distances: ' + error.message + '\n\nCheck the browser console (F12) for more details.');
            return hospitals;
        }
    };

    // Listen for sort changes from Sortbar
    useEffect(() => {
        const handleSortChange = async (e) => {
            const newSortType = e.detail.sortType;
            setSortType(newSortType);
            
            // Set type based on sort
            if (newSortType.includes('mri')) {
                setType('MRI');
            } else if (newSortType.includes('ct')) {
                setType('CT');
            }

            // If distance sort, prompt for address and calculate distances
            if (newSortType.includes('distance')) {
                const address = prompt('Please enter your address:');
                if (address) {
                    setUserAddress(address);
                } else {
                    return; // User cancelled
                }
            }
        };

        window.addEventListener('sortChange', handleSortChange);
        return () => window.removeEventListener('sortChange', handleSortChange);
    }, []);

    // Listen for search changes from Searchbar
    useEffect(() => {
        const handleSearchChange = (e) => {
            setSearchQuery(e.detail.query);
        };

        window.addEventListener('searchChange', handleSearchChange);
        return () => window.removeEventListener('searchChange', handleSearchChange);
    }, []);

    // Sort and filter data based on sort type and search query
    useEffect(() => {
        const sortData = async () => {
            let data = type === 'MRI' ? [...mriData] : [...ctData];
            
            // Apply search filter first
            if (searchQuery) {
                const queryLower = searchQuery.toLowerCase();
                data = data.filter(hospital =>
                    hospital.hospital.toLowerCase().includes(queryLower) ||
                    hospital.address.toLowerCase().includes(queryLower)
                );
            }
            
            if (sortType === 'mri-wait-time' || sortType === 'ct-wait-time') {
                // Sort by wait time (shortest to longest), with insufficient data at bottom
                data.sort((a, b) => {
                    // If both have valid data, sort by wait time
                    if (a.calculated_wait_time_days !== null && b.calculated_wait_time_days !== null) {
                        return a.calculated_wait_time_days - b.calculated_wait_time_days;
                    }
                    // If only one has valid data, it comes first
                    if (a.calculated_wait_time_days !== null) return -1;
                    if (b.calculated_wait_time_days !== null) return 1;
                    // Both have insufficient data, keep original order
                    return 0;
                });
                setDisplayData(data);
            } else if ((sortType === 'mri-distance' || sortType === 'ct-distance') && userAddress) {
                // Calculate distances using Google Maps API
                const sortedData = await calculateDistances(userAddress, data);
                // Ensure insufficient data hospitals stay at bottom after distance sort
                sortedData.sort((a, b) => {
                    // If both have valid wait times, sort by distance
                    if (a.calculated_wait_time_days !== null && b.calculated_wait_time_days !== null) {
                        return (a.calculatedDistance || 9999) - (b.calculatedDistance || 9999);
                    }
                    // If only one has valid data, it comes first
                    if (a.calculated_wait_time_days !== null) return -1;
                    if (b.calculated_wait_time_days !== null) return 1;
                    // Both have insufficient data, sort by distance
                    return (a.calculatedDistance || 9999) - (b.calculatedDistance || 9999);
                });
                setDisplayData(sortedData);
            } else {
                // Default: valid data first, insufficient data at bottom
                data.sort((a, b) => {
                    if (a.calculated_wait_time_days !== null && b.calculated_wait_time_days === null) return -1;
                    if (a.calculated_wait_time_days === null && b.calculated_wait_time_days !== null) return 1;
                    return 0;
                });
                setDisplayData(data);
            }
        };

        if (mriData.length > 0 || ctData.length > 0) {
            sortData();
        }
    }, [sortType, type, userAddress, mriData, ctData, googleMapsLoaded, searchQuery]);

    return (
        <div className="homepage-container" style={{paddingTop: '90px'}}>
            <h1>Diagnostic Imaging Wait Times</h1>
            
            {calculatingDistances && (
                <div style={{padding: '12px', background: '#2d2d2d', color: '#ececec', marginBottom: '20px', borderRadius: '8px', border: '1px solid #4a4a4a'}}>
                    Calculating distances... Please wait.
                </div>
            )}

            {loading ? (
                <div style={{color: '#ececec', textAlign: 'center', padding: '40px'}}>Loading...</div>
            ) : displayData.length === 0 ? (
                <div style={{color: '#a1a1a1', textAlign: 'center', padding: '40px'}}>No data found</div>
            ) : (
                <div style={{marginBottom: '60px'}}>
                    <div className="sort-indicator">
                        {sortType === 'mri-wait-time' && 'Sorted by: MRI Wait Times'}
                        {sortType === 'mri-distance' && userAddress && `Sorted by: MRI Distance from ${userAddress}`}
                        {sortType === 'mri-distance' && !userAddress && 'Sorted by: MRI Distance'}
                        {sortType === 'ct-wait-time' && 'Sorted by: CT Wait Times'}
                        {sortType === 'ct-distance' && userAddress && `Sorted by: CT Distance from ${userAddress}`}
                        {sortType === 'ct-distance' && !userAddress && 'Sorted by: CT Distance'}
                    </div>
                    <table className="wait-times-table">
                    <thead>
                        <tr>
                            <th>Imaging centre</th>
                            {(sortType.includes('distance')) && <th>Distance</th>}
                            <th>Address</th>
                            <th>Wait Time (Days)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map((h, i) => (
                            <tr key={i}>
                                <td>{h.hospital}</td>
                                {(sortType.includes('distance')) && <td>{h.distance}</td>}
                                <td>{h.address}</td>
                                <td>{h.calculated_wait_time_days !== null && h.calculated_wait_time_days !== '' ? Number(h.calculated_wait_time_days).toFixed(1) : 'Insufficient Data'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
}

export default Homepage;
